/**
 * Intereses REALES de factoring por mes, para la línea "Intereses" del ER v2 (debajo del EBIT).
 *
 * Helper PURO (sin fetch): las operaciones llegan de `getOperacionesFactoring(pais)`
 * (src/utils/factoring.js), que ya trae `costo_financiero`, `fecha_operacion`, `estado`,
 * `pais` y los embeds `proveedor:{nombre}` y `factura:{folio}`.
 *
 * Reglas (definidas en el scoping de Fase 3):
 *  - Importe = SOLO `costo_financiero` (el descuento financiero). NUNCA `comision_proveedor`
 *    (operativa), `iva_financiamiento` (impuesto) ni `getCostoEfectivo` (concepto de caja).
 *  - Filtro: `pais` de la sesión + `estado !== 'anulada'` (incluye activa + liquidada).
 *  - Mes: `fecha_operacion` (reconocimiento ENTERO en el mes de la operación, sin prorrateo),
 *    solo si el año coincide con el año del ER.
 *  - Moneda: `costo_financiero` YA es nativo en la moneda local del país (CLP/PEN) → alimenta
 *    directo la serie local. La conversión a USD (modo dólares/región) la hace el consumidor
 *    dividiendo por el TC del mes, igual que el resto de la línea.
 *
 * @param {object}   params
 * @param {Array}    params.operaciones - filas de operaciones_factoring (con embeds)
 * @param {string}   params.pais - "Chile" | "Perú"
 * @param {number}   params.ano - año del ER
 * @returns {{ mensual: number[], detalle: Object }}
 *   - mensual[m]: intereses de factoring del mes m (0..11), en moneda LOCAL.
 *   - detalle[m]: [{ proveedor, folio, fecha, interes }] — las operaciones que componen
 *     mensual[m], para que el total del modal cuadre con la celda por construcción.
 */
export const interesesFactoringPorMes = ({ operaciones, pais, ano }) => {
  const mensual = Array(12).fill(0);
  const detalle = {};

  (operaciones || []).forEach((op) => {
    if (op.estado === "anulada") return;
    if (op.pais !== pais) return; // defensivo: las ops ya vienen por país
    const interes = Number(op.costo_financiero) || 0;
    if (interes === 0) return; // sin interés no aporta ni al total ni al detalle

    const fechaStr = op.fecha_operacion ? String(op.fecha_operacion).split("T")[0] : null;
    if (!fechaStr) return;
    const [y, m] = fechaStr.split("-").map(Number);
    if (!y || !m || m < 1 || m > 12 || y !== ano) return;
    const idx = m - 1;

    mensual[idx] += interes;
    if (!detalle[idx]) detalle[idx] = [];
    detalle[idx].push({
      proveedor: op.proveedor?.nombre || "—",
      folio: op.factura?.folio ?? null,
      fecha: fechaStr,
      interes,
    });
  });

  return { mensual, detalle };
};
